import { z } from 'zod';
import { zfd } from 'zod-form-data';
import Payments from '~/stores/payments';
import api from '~/utils/api';
import {
  isCreditClaim,
  pendingClaimLineItemToCalculatedLineItem,
  pendingClaimsToClaimLineItemCreate,
} from '~/utils/claimLineItem';
import { ClaimType, ExchangeMethod } from '~/utils/constants';
import { toCurrency } from '~/utils/formatters';
import {
  makeCrewClaimAction,
  makeCrewClaimWithClaimLineItemsLoader,
  redirect,
} from '~/utils/routing';
import {
  creditResolutionMethodSchema,
  shipmentCreateSchema,
} from '~/utils/schemas';
import { getCreditMethods } from './returnSummaryCreditMethod';
import { getExchangeMethods } from './returnSummaryExchangeMethod';
import { getReturnSummaryParams } from './returnSummarySearchParams';

// ----------- Refund Methods -----------

const getOutstandingBalance = (
  total: number,
  refundMethod: z.infer<typeof creditResolutionMethodSchema> | null,
) => {
  // If the balance is positive, this indicates in favor of the merchant
  if (total > 0) {
    return {
      kind: 'Payment' as const,
      total,
      label: 'Payment due',
    };
  }

  if (total === 0 || !refundMethod) return null;

  return {
    total,
    kind: refundMethod,
    label: refundMethod === 'Refund' ? 'Refund owed' : 'Gift Card owed',
  };
};

export const { loader, useLoaderData } = makeCrewClaimWithClaimLineItemsLoader(
  async ({ params: { store }, context, request }, appStore) => {
    if (!context.crewClaim.returnMethod) {
      return redirect(`/${store}`);
    }

    const { settings, order, crewClaim } = context;
    const { currencyCode } = order;

    const params = getReturnSummaryParams(request.url);
    const creditMethod = getCreditMethods(
      context.settings,
      context.crewClaim.claimLineItems,
      params.creditMethod,
    );
    const exchangeMethod = getExchangeMethods(context, params.exchangeMethod);

    // ! It is expected at the API layer, than the claim line items will have the
    // ! creditMethod applied... should we apply it to the draft claim line item or keep intercepting as we are today
    const fromCustomerItems = crewClaim.claimLineItems;

    const returnSummary = await api.calculateBeforeClaimSubmission({
      params: { storefrontId: settings.storefrontId },
      body: {
        id: order.id,
        claimType: ClaimType.return,
        calculatedLineItems: fromCustomerItems.map((cli) =>
          isCreditClaim(cli) ?
            pendingClaimLineItemToCalculatedLineItem({
              ...cli,
              requestedResolutionMethodEnum:
                creditMethod.selected ?? cli.requestedResolutionMethodEnum,
            })
          : pendingClaimLineItemToCalculatedLineItem(cli),
        ),
        exchangeMethod: exchangeMethod.selected,
        returnShipment: crewClaim.returnMethod,
        toCustomerRate: crewClaim.toCustomerRate,
        creditResolutionMethod: creditMethod.selected ?? undefined,
      },
    });

    returnSummary.lineItemApplications.forEach(({ id, ...updates }) => {
      appStore.dispatch(
        appStore.actions.crewClaim.updateClaimLineItem({
          id,
          updates,
        }),
      );
    });

    const { balance, paymentIntent } = returnSummary;

    if (paymentIntent) {
      Payments.initializePayment(
        paymentIntent.clientSecret,
        paymentIntent.idFromPlatform,
      );
    }

    // TODO: it may make sense to include the payment intent in the balance object.. instead of separately
    // the max amount that can be can be charge to the customer
    const authorizedAmount = paymentIntent ? paymentIntent.amount : balance;

    const outstandingBalance = getOutstandingBalance(
      authorizedAmount,
      creditMethod.selected,
    );

    return {
      credits: returnSummary.credits,
      deductions: returnSummary.deductions,
      exchangeMethod,
      balance,
      creditMethod,
      authorizedAmount,
      paymentIntentSecret: paymentIntent?.clientSecret,
      outstandingBalance,
      currencyFormatter: toCurrency(currencyCode),
    };
  },
);

export enum FormKey {
  claimValues = 'claimValues',
  exchangeMethod = 'exchangeMethod',
  creditMethod = 'creditMethod',
}

const coerceAbsoluteNumber = z.coerce.number().transform(Math.abs);

const claimValues = z.object({
  feeName: z.string().optional(),
  paymentIntentSecret: z.string().optional(),
  incentive: coerceAbsoluteNumber,
  toCustomerShipping: coerceAbsoluteNumber,
  fromCustomerShipping: coerceAbsoluteNumber,
  fee: coerceAbsoluteNumber,
  balance: coerceAbsoluteNumber,
  authorizedAmount: coerceAbsoluteNumber,
});

export type ClaimValues = z.infer<typeof claimValues>;

const formDataSchema = zfd.formData({
  [FormKey.claimValues]: zfd.json(claimValues),
  [FormKey.exchangeMethod]: zfd.text(z.nativeEnum(ExchangeMethod).optional()),
  [FormKey.creditMethod]: zfd.text(creditResolutionMethodSchema.optional()),
});

export const { action, useActionResult } = makeCrewClaimAction(
  ({ context, formData, params: { store } }, appStore) => {
    const { storefrontId, stripeConfig } = context.settings;
    const {
      order,
      crewClaim: { address, returnMethod },
    } = context;

    if (!address) {
      return new Error('Context Error: Missing address');
    }

    if (!returnMethod) {
      return new Error('Context Error: Missing From Customer return method');
    }

    const formDataResult = formDataSchema.safeParse(formData);

    if (!formDataResult.success) {
      return new Error('Malformed form data', {
        cause: {
          error: formDataResult.error,
          entries: Array.from(Object.entries(formData)),
        },
      });
    }

    const {
      claimValues: {
        incentive: giftCardIncentiveAmountApplied,
        toCustomerShipping: exchangeOrderShippingAmountApplied,
        fromCustomerShipping: returnShippingAmountApplied,
        fee,
        feeName,
        balance,
        paymentIntentSecret,
        authorizedAmount,
      },
      exchangeMethod,
    } = formDataResult.data;

    const { id, email: customerEmail } = order;

    const claimLineItems = pendingClaimsToClaimLineItemCreate(
      context.crewClaim.claimLineItems,
    );

    const shouldConfirmPayment = !!paymentIntentSecret;

    const confirmation =
      shouldConfirmPayment ? Payments.confirmPayment() : Promise.resolve(null);

    return confirmation
      .then((paymentIntentId) =>
        api.createCrewClaim({
          params: { storefrontId },
          body: {
            claimLineItems,
            claimType: ClaimType.return,
            originalStoreOrderId: id,
            customerEmail,
            billingAddress: address,
            shippingAddress: address,
            giftCardIncentiveAmountApplied,
            exchangeOrderShippingAmountApplied,
            feeApplied: fee,
            feeName,
            returnShippingAmountApplied,
            source: 'Customer_App',
            // logically if shouldConfirmPayment is true, then paymentIntentId and stripeConfig should exist, but TS complains.
            ...(stripeConfig &&
              paymentIntentId &&
              shouldConfirmPayment && {
                customerPayment: {
                  idFromPlatform: paymentIntentId,
                  currencyCode: order.currencyCode,
                  stripeConfig,
                  // ! amount = non-refundable portion of the balance (IE, fees, shipping, etc.)
                  //! balance should be in dollars or the ratio equivalent of the currency
                  amount: balance,
                  // ! authorizedAmount = refundable portion of the balance (IE the instant exchange amount ) OR undefined if it does not exist
                  // ! We will know to capture immediately or wait based on the existence of authorizedAmount
                  authorizedAmount:
                    exchangeMethod === ExchangeMethod.instant ?
                      authorizedAmount
                    : undefined,
                },
              }),
          },
        }),
      )
      .then((claim) =>
        api
          .createShipment({
            params: { storefrontId },
            body: shipmentCreateSchema.parse({
              claimId: claim.id,
              returnShipment: returnMethod,
            }),
          })
          .then(() => claim),
      )
      .then((claim) => {
        appStore.dispatch(appStore.actions.completeCrewClaim());

        return redirect(`/${store}/claim/overview/${claim.externalId}`);
      })
      .catch((err) => ({
        ok: false,
        message: err instanceof Error ? err.message : String(err),
      }));
  },
);
